import React from 'react';
import { shortenNumber } from '../..';
import { BarDatum, BarLegendProps, ResponsiveBar } from '@nivo/bar';

import { theme } from '../../theme-dashboard';
import { HistogramData } from '../../types';
import { NoData } from '../no-data';

export const Histogram = ({
  data,
  isAxisDate,
  isYAxisPercentage,
  renderCustomTick,
  legends,
}: {
  data: HistogramData;
  isAxisDate?: boolean;
  isYAxisPercentage?: boolean;
  legends?: BarLegendProps[];
  // TODO: try to get type from nivo
  renderCustomTick?: (v: any) => React.JSX.Element;
}) => {
  const dash_theme = theme;
  const referenceValue = isAxisDate
    ? new Date(data.reference_line?.category_value || '')
    : data.reference_line?.category_value;

  // TODO: move formatDate to utils
  const formatDate = (value: number) => {
    const date = new Date(value);
    const year = date.getFullYear();
    const quarter = Math.floor(date.getMonth() / 3) + 1;
    return `Q${quarter} ${year}`;
  };

  const { barChartData } = data;

  if (barChartData.length === 0) {
    return <NoData />;
  }

  let date_category = false;

  let plot_data = barChartData.map((item) => {
    if (typeof item.category === 'string') {
      const timestamp = Date.parse(item.category);
      if (isNaN(timestamp)) {
        return item;
      }

      date_category = true;
      return { ...item, category: new Date(timestamp).getTime() };
    } else if (typeof item.category === 'string') {
      return { ...item, category: new Date(item.category).getTime() };
    }

    return item;
  }) as {
    category: string | number;
    value: number;
  }[];

  plot_data = [...plot_data].sort((a, b) => {
    const lhs = a.category;
    if (typeof lhs === 'string') {
      return lhs.localeCompare(b.category as string);
    }

    return lhs - (b.category as number);
  });

  return (
    <ResponsiveBar
      data={plot_data as unknown as BarDatum[]}
      indexBy="category"
      margin={{ top: 32, right: 40, bottom: 40, left: 60 }}
      padding={0.1}
      borderRadius={3}
      axisBottom={{
        renderTick: renderCustomTick
          ? (tick) => renderCustomTick(tick)
          : undefined,
        format: (d) => {
          if (!date_category) {
            return shortenNumber(d, 1);
          }
          return formatDate(d);
        },
        tickSize: 5,
        tickPadding: 10,
        tickValues: 4, //TODO: check the rules here
        tickRotation: 0,
        legend: '',
        legendPosition: 'end',
        legendOffset: 32,
      }}
      axisLeft={{
        format: (d) => (isYAxisPercentage ? `${d * 100}%` : d),
        tickSize: 10,
        tickPadding: 10,
        tickRotation: 0,
        tickValues: 5,
        legend: '',
        legendPosition: 'middle',
        legendOffset: -40,
        truncateTickAt: 0,
      }}
      enableLabel={false}
      enableGridX={false}
      enableGridY={false}
      gridXValues={10}
      theme={dash_theme}
      legends={legends}
      colors={
        data.reference_line
          ? (bar) => {
              return Number(bar.data.category || 0) < Number(referenceValue)
                ? '#04785766'
                : theme.tokens.emerald[700];
            }
          : theme.tokens.emerald[700]
      }
      //TODO: pass tooltip to separate component
      tooltip={({ value, indexValue }) => {
        const formattedDate = formatDate(Number(indexValue));

        return (
          <div
            style={{
              padding: 8,
              background: theme.tooltip.container.background,
            }}
          >
            <span>{isAxisDate ? formattedDate : indexValue}</span>:{' '}
            <strong>
              {isYAxisPercentage
                ? `${Number(shortenNumber(value, 2)) * 100}%`
                : shortenNumber(value, 2)}
            </strong>
          </div>
        );
      }}
    />
  );
};